<template>
  <div class="gp-image">
    <div class="container">
      <div class="lg-container" v-if="!fetchingData">
        <img
          class="lg-image"
          :src="`https://img.imageboss.me/cover:inside/1000x800/${image.url}`"
        />
        <div v-for="hotspot in hotspots" :key="`${hotspot.topDistance}`">
          <div
            class="lg-hotspot lg-hotspot--top-left"
            :style="hotSpotLocation(hotspot)"
          >
            <div class="lg-hotspot__button" :id="hotspot.htmlElementId" />
            <div class="lg-hotspot__label hotspot-wrapper">
              <div
                class="hotspot-section"
                v-for="(section, index) in hotspot.sections"
                :key="`section-${index}-${hotspot.topDistance}`"
                :class="{ leftborder: index > 0 }"
              >
                <div class="gp__properties" v-if="section.title">
                  <div class="font-light">
                    {{ !!section.subtitle ? section.subtitle : '&nbsp;' }}
                  </div>
                  <a
                    :href="section.titleUrl"
                    class="leading-normal font-medium mb-1"
                    @click="sendAdobeLinkNavClick($event, section)"
                  >
                    {{ section.title }}
                  </a>
                </div>
                <div class="hotspot-content">
                  <div
                    v-for="(content, index) in section.content"
                    :key="`${content.title}-${content.name}-${content.ctaUrl}`"
                    class="content-box"
                  >
                    <img
                      class="hotspot-image"
                      :src="hotSpotImageUrl(content.imageUrl)"
                      v-if="showHotspotImage(content.imageUrl, index)"
                    />
                    <div
                      class="font-light mt-2"
                      v-html="content.title"
                      v-if="showHotspotImage(content.imageUrl, index)"
                    ></div>
                    <div
                      class="gp__properties font-medium"
                      :class="{
                        lessspacing: !showHotspotImage(content.imageUrl, index),
                      }"
                    >
                      <a
                        :href="content.ctaUrl"
                        @click="sendAdobeLinkNavClick($event, content)"
                      >
                        {{ content.name }}
                        <strong class="h5 font-medium">&nbsp;&rsaquo;</strong>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { eventBus } from '../shared/event-bus';
import {
  handleAdobeNavEvent,
  fireAdobeAnalyticsEvent,
  adobeCleanString,
} from '../../helpers/adobe-analytics';

export default {
  name: 'GalleryProjectImage',
  props: {
    image: Object,
    imageIndex: Number,
    showHotspots: {
      default: true,
      required: false,
    },
  },
  data() {
    return {
      hotspots: [],
      fetchingData: false,
    };
  },
  mounted() {
    if (this.showHotspots) {
      this.fetchingData = true;
      this.getHotspotData();
    }
  },
  methods: {
    getHotspotData() {
      if (!!this.image.hotspots) {
        const allHotspots = [];
        this.image.hotspots.forEach(hotSpot => {
          allHotspots.push(
            axios.get(`/gallery/image_hotspot_data/${hotSpot.id}.json`)
          );
        });
        Promise.all(allHotspots).then(values => {
          const imageHotspotHtmlIds = [];
          values.forEach(({ data }) => {
            data.htmlElementId = `
              ${data.id}-${data.leftDistance}-${data.topDistance}`;
            imageHotspotHtmlIds.push(data.htmlElementId);
            this.hotspots.push(data);
          });
          this.fetchingData = false;
          this.$nextTick(() => {
            this.setEventListeners(imageHotspotHtmlIds);
          });
          eventBus.galleryProjectImageLoaded(true);
        });
      }
    },
    setEventListeners(ids) {
      // this function is what gets fired on click
      const selectHotspot = e => {
        const clickedHotspot = e.target.parentElement;
        const container = clickedHotspot.parentElement;
        // only include hotspots within same image to allow one open hotspot per image;
        const hotspots = container.querySelectorAll('.lg-hotspot');
        hotspots.forEach(hotspot => {
          if (hotspot === clickedHotspot) {
            hotspot.classList.toggle('lg-hotspot--selected');
          } else {
            hotspot.classList.remove('lg-hotspot--selected');
          }
        });
        this.sendAdobeHotspotOpen();
      };

      ids.forEach(id => {
        if (document.getElementById(id)) {
          document.getElementById(id).addEventListener('click', selectHotspot);
        }
      });
    },
    hotSpotLocation(hotspot) {
      return {
        top: `${Math.ceil(hotspot.topDistance * 100)}%`,
        left: `${Math.ceil(hotspot.leftDistance * 100)}%`,
      };
    },
    hotSpotImageUrl(image) {
      return image
        ? `https://img.imageboss.me/cover:center/125x150/${image}`
        : null;
    },
    showHotspotImage(image, index) {
      return !!image && index === 0;
    },
    sendAdobeHotspotOpen() {
      const imageInfo =
        this.image.caption || `${this.image.id} Image ${this.imageIndex + 1}`;
      const eventData = {
        type: 'click',
        detail: {
          eVar8: `INP|Gallery Project Hotspot Open|${adobeCleanString(
            imageInfo
          )}`,
        },
      };
      fireAdobeAnalyticsEvent(eventData);
    },
    sendAdobeLinkNavClick(event, property) {
      const title = property.title || property.name;
      const destinationUrl = property.titleUrl || property.ctaUrl;
      const navString = `NAV|Gallery Project Hotspot Link|${title}`;
      handleAdobeNavEvent({
        navString,
        destinationUrl,
        event,
      });
    },
  },
};
</script>
